// Shown to an ADMIN (not drivers -- see AppLayout for why it's scoped to
// the admin role only) when tenants.payment_restricted is true. They can
// still log in and fix payment, just not reach the real dashboard, per
// the original superadmin-scoping conversation: "if the payment fails,
// they should have the ability to log in and update or try the payment
// again, but not access their data."
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { CreditCard, LogOut, AlertTriangle } from 'lucide-react';

interface PaymentRequiredScreenProps {
  onLogout: () => void;
}

const PaymentRequiredScreen: React.FC<PaymentRequiredScreenProps> = ({ onLogout }) => {
  const [opening, setOpening] = useState(false);

  const openBillingPortal = async () => {
    setOpening(true);
    const { data, error } = await supabase.functions.invoke('create-billing-portal-session', {
      body: { return_url: window.location.href },
    });
    setOpening(false);
    if (error || data?.error || !data?.url) {
      toast({ title: 'Error', description: data?.error || error?.message || 'Failed to open billing portal', variant: 'destructive' });
      return;
    }
    window.location.href = data.url;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full shadow-lg bg-card border border-red-900/40">
        <CardHeader className="text-center pb-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-900/20 border border-red-900/40 mb-2">
            <AlertTriangle className="h-6 w-6 text-red-400" />
          </div>
          <CardTitle className="text-lg text-foreground">Payment needed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            Your last payment didn't go through. Update your card or retry payment to get back into your data -- nothing has
            been lost, this screen just needs clearing first.
          </p>
          <Button onClick={openBillingPortal} disabled={opening} className="golden-button w-full">
            <CreditCard className="h-4 w-4 mr-1.5" />
            {opening ? 'Opening...' : 'Update Payment'}
          </Button>
          <Button variant="outline" onClick={onLogout} className="w-full">
            <LogOut className="h-4 w-4 mr-1.5" />
            Log out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentRequiredScreen;
